import { m } from '@sohanscript/i18n';
import { formatDate, formatNumber } from '@sohanscript/lib/utils/format';
import { Chip } from '@sohanscript/ui/chip';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { Hydrate } from '@tanstack/react-start';
import { idle } from '@tanstack/react-start/hydration';
import { Suspense, useMemo } from 'react';
import { GoToTop } from '#/components/layout/goto-top.tsx';
import { GITHUB_LINK } from '#/components/layout/goto-top.utils.ts';
import { Link } from '#/components/link.tsx';
import { NoteContent } from '#/components/notes/note-content.tsx';
import { ShareButtons } from '#/components/notes/share-buttons.tsx';
import { SimilarReads } from '#/components/notes/similar-reads.tsx';
import { PageNav } from '#/components/page-nav.tsx';
import { Img } from '@/components/image';
import { extractHeadings } from '@/components/notes/table-of-contents.utils';
import { PageBreadcrumb } from '@/components/page-breadcrumb';
import { seo, siteConfig } from '@/lib/config/site';
import { api } from '@/lib/trpc/client';
import type { RouterOutputs } from '@/server/root';

export const Route = createFileRoute('/_main/note/$slug')({
	component: NoteDetailPage,
	async loader({ context, params }) {
		const [note] = await Promise.all([
			context.trpc.note.bySlug.ensureData({ slug: params.slug }),
			context.trpc.note.authorBySlug.ensureData({ slug: params.slug }),
			context.trpc.note.allSlugs.ensureData(),
		]);
		if (!note) throw notFound({ data: { message: m.note_not_found() } });
		return { note };
	},
	head({ loaderData, params }) {
		const note = loaderData?.note;
		const canonicalUrl = `${siteConfig.canonical}/note/${params.slug}`;
		const scripts = note
			? [
					{
						type: 'application/ld+json',
						children: JSON.stringify({
							'@context': 'https://schema.org',
							'@type': 'Article',
							headline: note.title,
							...(note.description ? { description: note.description } : {}),
							...(note.image ? { image: note.image } : {}),
							author: { '@type': 'Person', name: siteConfig.me },
							publisher: { '@type': 'Person', name: siteConfig.me },
							...(note.publishedAt ? { datePublished: note.publishedAt } : {}),
							...(note.updatedAt
								? { dateModified: note.updatedAt }
								: note.publishedAt
									? { dateModified: note.publishedAt }
									: {}),
							mainEntityOfPage: {
								'@type': 'WebPage',
								'@id': canonicalUrl,
							},
						}),
					},
				]
			: undefined;

		const tags = seo({
			title: note?.title ?? params.slug,
			description: note?.description ?? note?.body?.slice(0, 160),
			image: note?.image ?? siteConfig.ogImage,
			url: canonicalUrl,
			type: 'article',
			scripts,
		});

		return {
			links: [
				{
					rel: 'preload',
					href: '/fonts/souvenir.woff2',
					as: 'font',
					type: 'font/woff2',
					crossOrigin: 'anonymous',
				},
				...tags.links,
			],
			meta: tags.meta,
			scripts: tags.scripts,
		};
	},
});

function NoteDetailPage() {
	const { slug } = Route.useParams();
	return <NoteDetail slug={slug} />;
}

type NoteArticle = NonNullable<RouterOutputs['note']['bySlug']>;
type NoteAuthor = NonNullable<RouterOutputs['note']['authorBySlug']>;
type NoteNavItem = { slug: string; title: string };

function getNoteDetails(note: NoteArticle) {
	const tags = (note.tags ?? '')
		.split(',')
		.map((tag) => tag.trim())
		.filter(Boolean);
	const publishedDate =
		note.publishedAt || note.createdAt
			? formatDate(note.publishedAt || note.createdAt)
			: null;
	const readingTime = note.body
		? Math.ceil(note.body.trim().split(/\s+/).length / 200)
		: null;

	return { tags, publishedDate, readingTime };
}

function NoteHero({ image }: { image: string | null }) {
	if (!image) return null;
	return (
		<div className="corner-bevel mb-8 overflow-hidden rounded-xl">
			<Img
				src={image}
				width={800}
				className="aspect-video w-full object-cover"
			/>
		</div>
	);
}

function NoteArticleHeader({
	title,
	description,
	tags,
	publishedDate,
	readingTime,
}: {
	title: string;
	description: string | null;
	tags: string[];
	publishedDate: string | null;
	readingTime: number | null;
}) {
	return (
		<header className="mb-8">
			{(publishedDate || readingTime) && (
				<div className="flex items-center gap-3 text-primary/60 text-sm">
					{publishedDate && <time>{publishedDate}</time>}
					{publishedDate && readingTime && <span>·</span>}
					{readingTime && (
						<span>
							{m.reads_min_read({ count: formatNumber(readingTime) })}
						</span>
					)}
				</div>
			)}
			<h1 className="mt-2 font-bold text-3xl text-primary md:text-4xl">
				{title}
			</h1>
			{description && (
				<p className="mt-3 text-foreground/60 lg:text-lg">{description}</p>
			)}
			{tags.length > 0 && (
				<div className="mt-4 flex flex-wrap gap-2">
					{tags.map((tag) => (
						<Link
							key={tag}
							to="/notes"
							search={{ tag } as never}
							className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
						>
							<Chip className="transition-colors duration-300 hover:bg-primary/30">
								{tag}
							</Chip>
						</Link>
					))}
				</div>
			)}
		</header>
	);
}

function NoteAuthor({ author }: { author: NoteAuthor | null }) {
	if (!author) return null;
	return (
		<div className="mb-8 flex items-center gap-3 border-primary/10 border-b pb-8">
			{author.image && (
				<Img
					src={author.image}
					width={40}
					className="size-10 rounded-full object-cover"
				/>
			)}
			<div>
				<Link
					href={'/readme/sohanemon'}
					className="font-medium link text-primary text-sm"
				>
					{author.name}
				</Link>
				{author.bio && (
					<p className="text-foreground/60 text-xs">{author.bio}</p>
				)}
			</div>
		</div>
	);
}

function NoteArticleBody({
	body,
	headings,
}: {
	body: string | null;
	headings: ReturnType<typeof extractHeadings>;
}) {
	if (!body) return null;
	return (
		<Hydrate when={idle()}>
			<NoteContent content={body} headings={headings} />
		</Hydrate>
	);
}

function NoteShareFooter({
	title,
	slug,
	items,
}: {
	title: string;
	slug: string;
	items: NoteNavItem[];
}) {
	return (
		<>
			<div className="mt-16 flex flex-col items-center gap-4 border-primary/10 border-t pt-10 text-center">
				<p className="font-medium text-foreground/40 text-xs uppercase tracking-widest">
					{m.note_share_cta()}
				</p>
				<Hydrate when={idle()}>
					<ShareButtons title={title} slug={slug} />
				</Hydrate>
			</div>
			<Hydrate when={idle()}>
				<PageNav
					currentSlug={slug}
					itemTo="/note/$slug"
					listTo="/notes"
					listLabel={m.note_all()}
					items={items}
					className="mt-8 border-primary/10 border-t pt-8"
				/>
			</Hydrate>
		</>
	);
}

function NoteDetail({ slug }: { slug: string }) {
	const [note] = api.note.bySlug.useSuspenseQuery({ slug });
	const [author] = api.note.authorBySlug.useSuspenseQuery({ slug });
	const [allSlugs] = api.note.allSlugs.useSuspenseQuery();

	const items = (allSlugs ?? []).map((a) => ({
		slug: a.slug?.current ?? '',
		title: a.title ?? '',
	}));

	const { tags, publishedDate, readingTime } = getNoteDetails(note);

	const headings = useMemo(
		() => (note.body ? extractHeadings(note.body) : []),
		[note.body],
	);

	return (
		<div className="pt-16 pb-10">
			<PageBreadcrumb
				currentTitle={note.title}
				currentSlug={slug}
				listTo="/notes"
				listLabel={m.note_all()}
				itemTo="/note/$slug"
				items={items}
			/>
			<section className="container min-w-0">
				<NoteHero image={note.image} />
				<NoteArticleHeader
					title={note.title}
					description={note.description}
					tags={tags}
					publishedDate={publishedDate}
					readingTime={readingTime}
				/>

				<NoteAuthor author={author} />
				<NoteArticleBody body={note.body} headings={headings} />

				<NoteShareFooter title={note.title ?? ''} slug={slug} items={items} />
				<Suspense>
					<SimilarReads tags={tags} currentSlug={slug} />
				</Suspense>
			</section>
			<GoToTop
				links={[
					{
						href: '/notes',
						icon: 'solar:document-add-linear',
						label: m.note_all(),
					},
					{
						href: '#similar-reads',
						icon: 'lucide:book-open',
						label: m.note_similar_label(),
					},
					GITHUB_LINK,
				]}
			/>
		</div>
	);
}
